import { Market } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useEffect, useState } from 'react';
import {
  GetSuggestionValue,
  InputProps,
  OnSuggestionsClearRequested,
  OnSuggestionSelected,
  ShouldRenderSuggestions,
  SuggestionsFetchRequested,
} from 'react-autosuggest';
import { Text } from '@components/Text/Text.component';
import { Autocomplete } from '@form/components/Autocomplete/Autocomplete.component';
import { useTranslation } from '@locales/useTranslation.hook';
import Fuse from 'fuse.js';

type Props = Omit<ComponentPropsWithRef<'div'>, 'onSelect'> & {
  markets: Market[];
  value?: Market;
  onSelect: (market: Market) => void;
  isLeftIconStyle?: boolean;
};

function renderSuggestion(suggestion: Market) {
  return <Text variant="small">{suggestion.marketName}</Text>;
}

const MarketAutocomplete: FC<Props> = ({ isLeftIconStyle = false, markets, value, onSelect, ...rest }) => {
  const [autocompleteValue, setAutocompleteValue] = useState(value ? value.marketName : '');
  const [suggestedMarkets, setSuggestedMarkets] = useState<Market[]>([]);
  const { t } = useTranslation(['stocks']);

  useEffect(() => {
    setAutocompleteValue(value ? value.marketName : '');
  }, [value]);

  const onSuggestionsFetchRequested: SuggestionsFetchRequested = ({ value: val, reason }) => {
    if (val.length === 0 || reason === 'input-focused') {
      setAutocompleteValue('');
      setSuggestedMarkets(markets);
    } else {
      const fuse = new Fuse(markets, { keys: ['marketName'], threshold: 0.2 });
      setSuggestedMarkets(fuse.search(val));
    }
  };

  const onSuggestionsClearRequested: OnSuggestionsClearRequested = () => {
    setSuggestedMarkets([]);
  };

  const shouldRenderSuggestions: ShouldRenderSuggestions = () => {
    return true;
  };

  const getSuggestionValue: GetSuggestionValue<Market> = (market) => {
    return market.marketName;
  };

  const onSuggestionSelected: OnSuggestionSelected<Market> = (_, { suggestion }) => {
    onSelect(suggestion);
  };

  const onInputChange: InputProps<Market>['onChange'] = (_, { newValue }) => {
    setAutocompleteValue(newValue);
  };

  const inputProps = {
    id: 'stocks-live-cattle-market-autocomplete-input',
    label: isLeftIconStyle ? '' : t('stocks.live-cattle.autocomplete.label'),
    onChange: onInputChange,
    placeholder: t('stocks.live-cattle.autocomplete.placeholder'),
    placeholderVariant: isLeftIconStyle ? 'new-small' : 'small',
    value: autocompleteValue,
  };

  return (
    <div {...rest}>
      <Autocomplete
        getSuggestionValue={getSuggestionValue}
        inputProps={inputProps}
        isLeftIconStyle={isLeftIconStyle}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        renderSuggestion={renderSuggestion}
        shouldRenderSuggestions={shouldRenderSuggestions}
        suggestions={suggestedMarkets.sort((a, b) => a.marketName.localeCompare(b.marketName))}
      />
    </div>
  );
};

export { MarketAutocomplete };
